import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { adminBlogStatsOverview } from "@/lib/blog-stats.functions";
import { BlogSubNav } from "@/components/admin/BlogSubNav";
import { BarChart3, Eye, MousePointerClick, TrendingUp, Target, FileText, ExternalLink, AlertTriangle } from "lucide-react";
import { TrendCharts } from "@/components/social/TrendCharts";

export const Route = createFileRoute("/admin/blog-stats")({
  component: BlogStatsPage,
});

const KPI = [
  { bg: "bg-blue-500/5", ring: "ring-blue-500/20", text: "text-blue-600", icon: "text-blue-500" },
  { bg: "bg-amber-500/5", ring: "ring-amber-500/20", text: "text-amber-600", icon: "text-amber-500" },
  { bg: "bg-emerald-500/5", ring: "ring-emerald-500/20", text: "text-emerald-600", icon: "text-emerald-500" },
  { bg: "bg-primary/5", ring: "ring-primary/20", text: "text-primary", icon: "text-primary" },
];

function Kpi({ icon: Icon, label, value, tone }: { icon: any; label: string; value: string; tone: number }) {
  const s = KPI[tone % KPI.length];
  return (
    <div className={`rounded-xl border border-border ${s.bg} ring-1 ${s.ring} p-4`}>
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <Icon className={`h-4 w-4 ${s.icon}`} /> {label}
      </div>
      <div className={`mt-1 font-serif text-3xl ${s.text}`}>{value}</div>
    </div>
  );
}

function BlogStatsPage() {
  const fn = useServerFn(adminBlogStatsOverview);
  const [days, setDays] = useState<30 | 90>(30);
  const { data, isLoading } = useQuery({
    queryKey: ["blog-stats", days],
    queryFn: () => fn({ data: { days } }),
  });

  const totals = data?.totals ?? { clicks: 0, impressions: 0, ctr: 0, posts: 0, tracked: 0 };
  const posts = data?.posts ?? [];
  const series = data?.series ?? [];

  return (
    <div>
      <BlogSubNav />

      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-serif text-2xl text-primary flex items-center gap-2">
            <BarChart3 className="h-6 w-6 text-accent" /> Blog stats
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {isLoading
              ? "Loading…"
              : `${totals.posts} live post${totals.posts === 1 ? "" : "s"} · ${totals.tracked} matched to Search Console.`}
          </p>
        </div>
        <div className="inline-flex rounded-lg border border-border overflow-hidden text-xs">
          {[30, 90].map((d) => (
            <button
              key={d}
              onClick={() => setDays(d as 30 | 90)}
              className={`px-3 py-1.5 ${days === d ? "bg-primary text-primary-foreground" : "bg-background text-muted-foreground hover:bg-muted"}`}
            >
              Last {d} days
            </button>
          ))}
        </div>
      </div>

      {data && !data.connected && (
        <div className="mt-4 flex items-start gap-2 rounded-lg border border-amber-500/30 bg-amber-500/5 p-3 text-xs text-amber-800 dark:text-amber-200">
          <AlertTriangle className="mt-0.5 h-4 w-4" />
          <div>
            Google Search Console isn't connected — stats will be zero.
            {data.reason ? <span className="ml-1 opacity-80">({data.reason})</span> : null}{" "}
            Connect it in{" "}
            <Link to="/admin/seo/integrations" className="underline">
              SEO → Integrations
            </Link>
            .
          </div>
        </div>
      )}

      <div className="mt-5 grid gap-3 md:grid-cols-4">
        <Kpi icon={Eye} label="Impressions" value={totals.impressions.toLocaleString()} tone={0} />
        <Kpi icon={MousePointerClick} label="Clicks" value={totals.clicks.toLocaleString()} tone={1} />
        <Kpi icon={TrendingUp} label="CTR" value={`${(totals.ctr * 100).toFixed(2)}%`} tone={2} />
        <Kpi icon={FileText} label="Live posts" value={String(totals.posts)} tone={3} />
      </div>

      <div className="mt-6 rounded-xl border border-border bg-card p-5">
        <div className="font-serif text-lg text-primary mb-3 flex items-center gap-2">
          <TrendingUp className="h-5 w-5 text-accent" /> Trends
        </div>
        {isLoading ? (
          <p className="text-sm text-muted-foreground">Loading trends…</p>
        ) : (
          <TrendCharts data={series} />
        )}
      </div>

      <div className="mt-6 rounded-xl border border-border bg-card p-5">
        <div className="font-serif text-lg text-primary mb-3">Per-post performance</div>
        {posts.length === 0 ? (
          <p className="text-sm text-muted-foreground">No published posts yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-muted-foreground border-b border-border">
                  <th className="py-2">Post</th>
                  <th className="py-2 text-right">Impressions</th>
                  <th className="py-2 text-right">Clicks</th>
                  <th className="py-2 text-right">CTR</th>
                  <th className="py-2 text-right">Avg. pos</th>
                  <th className="py-2 text-right"></th>
                </tr>
              </thead>
              <tbody>
                {posts.map((p) => (
                  <tr key={p.id} className="border-b border-border/50 last:border-0">
                    <td className="py-2 pr-3">
                      <Link
                        to="/admin/blog-stats/$id"
                        params={{ id: p.id }}
                        className="font-medium text-primary hover:underline"
                      >
                        {p.title}
                      </Link>
                      <div className="text-xs text-muted-foreground">
                        /{p.slug} {p.category ? `· ${p.category}` : ""}
                        {!p.url && <span className="ml-1 text-amber-600">· not in GSC yet</span>}
                      </div>
                    </td>
                    <td className="py-2 text-right font-mono">{p.impressions.toLocaleString()}</td>
                    <td className="py-2 text-right font-mono">{p.clicks.toLocaleString()}</td>
                    <td className="py-2 text-right font-mono">{(p.ctr * 100).toFixed(2)}%</td>
                    <td className="py-2 text-right font-mono">{p.avgPosition > 0 ? p.avgPosition.toFixed(1) : "—"}</td>
                    <td className="py-2 text-right">
                      <a
                        href={`/blog/${p.slug}`}
                        target="_blank"
                        rel="noopener"
                        className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-primary"
                      >
                        <ExternalLink className="h-3 w-3" />
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p className="mt-3 text-xs text-muted-foreground flex items-center gap-1">
          <Target className="h-3 w-3" /> Data pulled from Google Search Console. Posts missing here haven't been
          crawled or indexed yet.
        </p>
      </div>
    </div>
  );
}
